export type NavItem = {
  label: string;
  path: string;
};

export type SiteSettings = {
  businessName: string;
  shortBusinessName: string;
  phoneDisplay: string;
  phoneHref: string;
  email: string;
  serviceArea: string;
  serviceAreaShortList: string[];
  topBarText: string;
  heroEyebrow: string;
  heroTitle: string;
  heroDescription: string;
  heroImage: string;
  heroImageAlt: string;
  trustPoints: string[];
  whyChooseTitle: string;
  whyChooseDescription: string;
  whyChoosePoints: string[];
  whyChooseImage: string;
  whyChooseImageAlt: string;
  recentWorkTitle: string;
  recentWorkDescription: string;
  reviewsTitle: string;
  reviewsDescription: string;
  serviceAreaTitle: string;
  serviceAreaDescription: string;
  freeEstimateTitle: string;
  freeEstimateDescription: string;
  servicesPageTitle: string;
  servicesPageDescription: string;
  aboutTitle: string;
  aboutDescription: string;
  aboutStory: string[];
  aboutImages: { src: string; alt: string }[];
  valuesTitle: string;
  values: { title: string; text: string }[];
  galleryTitle: string;
  galleryDescription: string;
  reviewsPageTitle: string;
  reviewsPageDescription: string;
  contactTitle: string;
  contactDescription: string;
  propertyTypes: string;
  footerDescription: string;
  copyrightYear: string;
  formspree: string;
  facebookReviews: string;
};

export type ServiceItem = {
  name: string;
  description: string;
  image: string;
};

export type ServiceGroup = {
  title: string;
  items: ServiceItem[];
};

export type GalleryImage = {
  src: string;
  alt: string;
};

export type Review = {
  name: string;
  text: string;
};

export type SiteContent = {
  site: SiteSettings;
  navItems: NavItem[];
  serviceGroups: ServiceGroup[];
  galleryImages: GalleryImage[];
  reviews: Review[];
};

export const defaultSiteContent: SiteContent = {
  site: {
    businessName: 'B&B Lawn & Property Maintenance LLC',
    shortBusinessName: 'B&B Lawn & Property Maintenance',
    phoneDisplay: '(937) 215-7863',
    phoneHref: 'tel:+19372157863',
    email: 'bbproperty2024@outlook.com',
    serviceArea: 'Springfield, Ohio and surrounding areas',
    serviceAreaShortList: ['Springfield', 'New Carlisle', 'Enon', 'Surrounding Areas'],
    topBarText: 'Residential & Commercial Property Services in Springfield, Ohio and Surrounding Areas',
    heroEyebrow: 'Reliable. Local. Done Right.',
    heroTitle: 'Reliable Lawn Care and Property Maintenance in Springfield, Ohio',
    heroDescription:
      'B&B Lawn & Property Maintenance LLC provides dependable residential and commercial service for mowing, landscaping, mulching, edging, fencing, hauling, power washing, lighting, and more.',
    heroImage: '/images/striped-lawn-wide.jpg',
    heroImageAlt: 'Freshly striped lawn',
    trustPoints: [
      'Residential & Commercial',
      'Free Estimates',
      'Reliable Service',
      'Quality Workmanship',
      'Springfield & Surrounding Areas',
    ],
    whyChooseTitle: 'Dependable work, clean results, and local service you can trust',
    whyChooseDescription:
      'B&B Lawn & Property Maintenance LLC provides reliable residential and commercial services throughout Springfield, Ohio and surrounding areas. We take pride in delivering quality work, dependable service, and clean results that help keep properties looking their best.',
    whyChoosePoints: [
      'Dependable service',
      'Quality workmanship',
      'Real local experience',
      'Residential and commercial work',
      'Attention to detail',
      'Free estimates',
    ],
    whyChooseImage: '/images/truck-trailer-lawn.jpg',
    whyChooseImageAlt: 'B&B truck and trailer',
    recentWorkTitle: 'Real jobs. Real results. No stock-photo nonsense.',
    recentWorkDescription:
      'Take a look at some of our recent lawn care, landscaping, fencing, lighting, and property maintenance work.',
    reviewsTitle: 'What our customers say',
    reviewsDescription:
      'We are proud to earn the trust of our customers through dependable service, quality work, and strong results.',
    serviceAreaTitle: 'Serving Springfield, Ohio and surrounding areas',
    serviceAreaDescription:
      'B&B Lawn & Property Maintenance LLC proudly serves Springfield, Ohio and nearby communities with dependable residential and commercial lawn care, landscaping, and property maintenance services.',
    freeEstimateTitle: 'Request a free estimate',
    freeEstimateDescription:
      'Need lawn care, landscaping, fencing, hauling, lighting, or property maintenance service? Contact B&B Lawn & Property Maintenance LLC today for a free estimate.',
    servicesPageTitle: 'Lawn care, property maintenance, and specialty services',
    servicesPageDescription:
      'From weekly mowing to fencing, hauling, lighting, and cleanup work, B&B handles the kind of jobs local property owners actually need done.',
    aboutTitle: 'Local property service focused on doing the job right',
    aboutDescription:
      'B&B Lawn & Property Maintenance LLC provides reliable residential and commercial services throughout Springfield, Ohio and surrounding areas. We take pride in delivering quality work, dependable service, and clean results that help keep properties looking their best.',
    aboutStory: [
      'Our company was founded in 2023 by Blake Wirick and his father-in-law, Bryan Silvers. After spending many years working together at Honda, we decided to take a leap of faith and pursue our shared passion for working outdoors by starting our own business.',
      'Blake spent 10 years with Honda, while Bryan dedicated more than 30 years to the company. Through those years, we developed a strong work ethic, attention to detail, and a commitment to providing quality service - values we now bring to every project we take on.',
      'As a family-owned and operated business, we take pride in helping homeowners maintain and improve their properties. We offer a wide range of services including landscaping, lawn mowing, power washing, fence installation, and general home maintenance.',
      'Our goal is simple: provide reliable, high-quality work while building lasting relationships with our customers. We look forward to earning your trust and helping with your next project.',
    ],
    aboutImages: [
      { src: '/images/truck-trailer-lawn.jpg', alt: 'B&B truck and trailer' },
      { src: '/images/mulch-side-yard.jpg', alt: 'Mulch along side yard' },
      { src: '/images/fence.jpg', alt: 'Fence installation' },
    ],
    valuesTitle: 'What customers can expect',
    values: [
      {
        title: 'Reliable service',
        text: 'Clear communication, dependable scheduling, and work that gets finished the right way.',
      },
      {
        title: 'Quality workmanship',
        text: 'Clean mowing lines, sharp beds, solid fence work, and details that improve curb appeal.',
      },
      {
        title: 'Residential and commercial',
        text: 'Service for homeowners, rental properties, and commercial spaces across the area.',
      },
    ],
    galleryTitle: 'Recent work from B&B Lawn & Property Maintenance',
    galleryDescription:
      'Lawn stripes, mulch work, fencing, lighting, cleanup, deck work, and other jobs completed across Springfield and surrounding areas.',
    reviewsPageTitle: 'Real feedback from real customers',
    reviewsPageDescription:
      'These reviews highlight the kind of work B&B is known for: fast response, strong communication, quality results, and solid follow-through.',
    contactTitle: 'Request a free estimate',
    contactDescription:
      'Need lawn care, landscaping, fencing, hauling, lighting, or general property maintenance service? Reach out and tell us what you need.',
    propertyTypes: 'Residential and Commercial',
    footerDescription:
      'B&B Lawn & Property Maintenance LLC provides reliable lawn care, landscaping, lighting, hauling, and property maintenance for residential and commercial properties in Springfield, Ohio and surrounding areas.',
    copyrightYear: '2026',
    formspree: 'https://formspree.io/f/mqeyqlby',
    facebookReviews: 'https://www.facebook.com/profile.php?id=61556313150600&sk=reviews',
  },
  navItems: [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services' },
    { label: 'About', path: '/about' },
    { label: 'Gallery', path: '/gallery' },
    { label: 'Reviews', path: '/reviews' },
    { label: 'Contact', path: '/contact' },
  ],
  serviceGroups: [
    {
      title: 'Lawn & Landscape Services',
      items: [
        {
          name: 'Mowing',
          description: 'Clean, reliable mowing service to keep your property looking sharp.',
          image: '/images/striped-lawn-wide.jpg',
        },
        {
          name: 'Landscaping',
          description: 'Landscape improvements and upkeep that help your property stand out.',
          image: '/images/aerial-property.jpg',
        },
        {
          name: 'Mulching',
          description: 'Fresh mulch installation for a clean, finished look around beds and trees.',
          image: '/images/mulch-under-deck.jpg',
        },
        {
          name: 'Edging',
          description: 'Defined edges that give your lawn and landscape a crisp appearance.',
          image: '/images/lawn-sidewalk.jpg',
        },
      ],
    },
    {
      title: 'Property & Exterior Services',
      items: [
        {
          name: 'Power Washing',
          description: 'Exterior cleaning that helps restore the look of siding, decks, driveways, and more.',
          image: '/images/deck-collage.jpg',
        },
        {
          name: 'Hauling',
          description: 'Fast debris, cleanup, and material hauling for homes and businesses.',
          image: '/images/dump.jpg',
        },
        {
          name: 'Dumpster Rental',
          description: 'Convenient dumpster rental options to help keep larger cleanup jobs moving.',
          image: '/images/dump.jpg',
        },
      ],
    },
    {
      title: 'Specialty Services',
      items: [
        {
          name: 'Fencing',
          description: 'Fence and gate work built with durability and quality in mind.',
          image: '/images/fence.jpg',
        },
        {
          name: 'Holiday Lighting',
          description: 'Seasonal lighting that adds curb appeal and takes the hassle off your plate.',
          image: '/images/red-lighting-porch.jpg',
        },
        {
          name: 'Permanent Lighting',
          description: 'Professional permanent lighting solutions for year-round beauty and convenience.',
          image: '/images/holiday-lighting-house.jpg',
        },
      ],
    },
  ],
  galleryImages: [
    { src: '/images/striped-lawn-wide.jpg', alt: 'Fresh striped lawn mowing work' },
    { src: '/images/lawn-sidewalk.jpg', alt: 'Trimmed lawn along sidewalk and home' },
    { src: '/images/mulch-under-deck.jpg', alt: 'Fresh black mulch installed under deck' },
    { src: '/images/mulch-patio.jpg', alt: 'Mulch area around patio seating' },
    { src: '/images/mulch-side-yard.jpg', alt: 'Mulch bed installed along side of home' },
    { src: '/images/fence.jpg', alt: 'New wood fence installation' },
    { src: '/images/deck-steps.jpg', alt: 'Deck and stair construction work' },
    { src: '/images/deck-collage.jpg', alt: 'Deck restoration and power washing collage' },
    { src: '/images/truck-trailer-lawn.jpg', alt: 'B&B truck and trailer parked near striped lawn' },
    { src: '/images/aerial-property.jpg', alt: 'Aerial view of maintained residential property' },
    { src: '/images/holiday-lighting-house.jpg', alt: 'Permanent lighting installed on large home' },
    { src: '/images/red-lighting-porch.jpg', alt: 'Red holiday lighting on front porch' },
    { src: '/images/before-after-bushes.jpg', alt: 'Before and after shrub and bed cleanup' },
  ],
  reviews: [
    {
      name: 'Connie Miller',
      text: 'Late January we were hit with over 10 inches of snow and many people were snowed in. Blake and his partner came to the rescue. They were thoughtful, professional, and willing to do extra snow removal, including clearing a path in the yard for my dog. I would recommend B&B Property Maintenance without hesitation.',
    },
    {
      name: 'Clint Carosiellie',
      text: '100% the best. I had two huge mulch piles that needed removed from some trees I had cut down. B&B said no problem and had it done in no time. I called, got an estimate appointment, and two days later the problem was solved. They got the job done right the first time.',
    },
    {
      name: 'Bre Monea',
      text: 'They did leaf removal the same day at a very reasonable price, especially since I live in a heavily wooded area. They worked fast and it looks great. I will be using them again.',
    },
    {
      name: 'Lori Burr',
      text: 'Blake truly goes above and beyond for his customers and truly cares about providing excellent customer service. I highly recommend B&B Lawn & Property Maintenance.',
    },
    {
      name: 'Chris White',
      text: 'Blake did an incredible job rebuilding our gate and fence. It is now secure and sturdy, and the gate opens freely. We truly appreciate the dedication to quality craftsmanship.',
    },
  ],
};

export function getFeaturedServices(content: SiteContent): ServiceItem[] {
  return [
    ...content.serviceGroups[0].items,
    content.serviceGroups[2].items[0],
    content.serviceGroups[1].items[0],
  ].filter(Boolean);
}

export function getServiceOptions(content: SiteContent): string[] {
  return content.serviceGroups.flatMap((group) => group.items.map((item) => item.name));
}
